import { ConfigProvider, Table, Tooltip } from 'antd'
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server.ts'
import SearchIcon from '@/assets/icons/search.svg?react'
import ConfigureIcon from '@/assets/image-view/configure.svg'
import { ButtonRadio, EmptyView, Loading, Pagination, Radio, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { GhostType, MachineType, PaymentType } from '@/consts/machine'
import {
  BILLING_METHODS,
  type BillingMethod,
  DURATION,
  type Duration,
  ItemPaymentType,
} from '@/consts/order'
import { Image } from '@/models/image.ts'
import { Machine } from '@/models/machine'
import { Snapshot } from '@/models/snapshot.ts'
import { ImageSelector } from '@/pages/create-instance/components/image-selector.tsx'
import { createColumns } from '@/pages/create-instance/components/machine-table-columns.tsx'
import { SnapshotSelector } from '@/pages/create-instance/components/snapshot-selector.tsx'
import { getMinimumInstanceCountForBootSource } from '@/pages/create-instance/utils/extraStorage'
import { useUserStore } from '@/stores/user'
import { formatDomain } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { useSelectionScope } from '../hooks/useSelectionScope'
import { ImageAttributeSelection } from '../utils/imageFilters'
import { AcceleratorTypeSelector } from './AcceleratorTypeSelector'
import { ImageAttributeFilters } from './ImageAttributeFilters'
import { ImageKeywordSearch } from './ImageKeywordSearch'
import { WorkloadTypeSelector } from './WorkloadTypeSelector'

const HIDE_DOMAINS = (import.meta.env.VITE_HIDE_DOMAINS ?? '')
  .split(',')
  .map((s: string) => parseInt(s, 10))
  .filter((n: number) => !Number.isNaN(n))

const GPU_MACHINE_SPECS = {
  inference: ['NVIDIAGeForceRTX5090', 'NVIDIAGeForceRTX4090'],
  training: ['NVIDIAH100', 'NVIDIAH200', 'NVIDIAL40', 'NVIDIAA100', 'NVIDIARTXPRO6000'],
  render: ['NVIDIARTX6000Ada', 'NVIDIAGeForceRTX4090', 'NVIDIARTX5000Ada'],
}

const SPECIAL_CLIENT_MACHINE_SPECS = [
  {
    userId: 2010,
    inference: [],
    training: [
      'NVIDIAH100SXM5-81559MB*1_INTEL(R)XEON(R)PLATINUM8558*16_RAM229376MB_DISK1000GB_PRICE3800000MC',
    ],
    render: [],
    cpu: [],
  },
  {
    userId: 1568, // frank
    inference: [],
    training: [
      'NVIDIAH100SXM5-81559MB*1_INTEL(R)XEON(R)PLATINUM8558*16_RAM229376MB_DISK1000GB_PRICE3800000MC',
    ],
    render: [],
    cpu: [],
  },
]

const getRowKey = (m: any) => {
  const domain = Array.isArray(m.domains) ? m.domains[0] : m.domain
  return `${m.key}-${domain ?? 'na'}`
}

interface MachineSelectorProps<T extends Snapshot = Snapshot> {
  imageResId?: string
  type: MachineType
  period: PaymentType
  onTypeChange: (type: MachineType) => void
  onChange: (value: Machine | undefined) => void
  count: number
  checkedImage?: Image
  setCheckedImage: (value?: Image) => void
  checkedSnap?: T
  setCheckedSnap: (value?: T) => void
  onMachinesChange?: (value: Machine[]) => void
  onImagesChange?: (value: Image[]) => void
  onSnapshotsChange?: (value: T[]) => void
  selectedMachine?: Machine
  selectedImage?: Image
  selectedGhostType?: GhostType
  workloadTypes?: (string | number)[]
  teamSnapshots?: T[]
  enforceBootSourceResourceLimit?: boolean
  supportsExtraResourcesForBootSource?: boolean
  billingMethod?: BillingMethod
  duration?: Duration
  imageEmptyMessageVariant?: 'default' | 'team'
  paginateImagesOnServer?: boolean
  toolbarStart?: ReactNode
  imageFiltersInToolbar?: boolean
}

const REGION_GLOBAL = -1
const REGION_DATA_DRIVE = -2
const REGION_MESH_CLUSTERING = -3

function getMachineRowKey(machine: Machine & { rowKey?: string }) {
  return machine.rowKey || machine.key
}

function getMachineAiState(machine: Machine & { rowKey?: string }, selected: boolean) {
  if (machine.maxAvailUnits === 0) return 'out-of-stock'
  return selected ? 'selected' : 'available'
}

export function MachineSelector<T extends Snapshot = Snapshot>({
  imageResId,
  type,
  period,
  onTypeChange,
  onChange,
  count,
  checkedImage,
  setCheckedSnap,
  checkedSnap,
  setCheckedImage,
  onMachinesChange,
  selectedMachine,
  onImagesChange,
  onSnapshotsChange,
  selectedGhostType,
  workloadTypes: initialWorkloadTypes,
  teamSnapshots,
  enforceBootSourceResourceLimit = false,
  supportsExtraResourcesForBootSource = false,
  billingMethod,
  duration,
  imageEmptyMessageVariant = 'default',
  paginateImagesOnServer = false,
  toolbarStart,
  imageFiltersInToolbar = false,
}: MachineSelectorProps<T>) {
  const { t } = useTranslation()
  const scope = useSelectionScope()
  const [imageFilters, setImageFilters] = useState<ImageAttributeSelection>({})
  const [showImageFilters, setShowImageFilters] = useState(true)
  const requestIdRef = useRef(0)
  const userId = useUserStore((state) => state.id)
  const [loading, setLoading] = useState(false)
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [rawMachineClasses, setRawMachineClasses] = useState<
    { category: MachineType; classes: any[] }[]
  >([])
  const [allMachines, setAllMachines] = useState<Machine[]>([])
  const [filteredMachines, setFilteredMachines] = useState<Machine[]>([])
  const [page] = useState(1)
  const [ghostType, setGhostType] = useState(GhostType.Image)
  const [bootSourceSearch, setBootSourceSearch] = useState('')
  const [imageKeywords, setImageKeywords] = useState<string[]>([])
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])
  const [regions, setRegions] = useState<number[]>([])
  const [regionFilterCriteria, setRegionFilterCriteria] = useState<number>(REGION_GLOBAL)
  const [isVgpuChecked, setIsVgpuChecked] = useState(false)
  const [workloadTypes, setWorkloadTypes] = useState<(string | number)[]>(
    initialWorkloadTypes ?? [MachineType.TRAINING_GPU],
  )

  const pageSize = 99
  const selectedBootSourceLabel = checkedImage?.resID
    ? `${checkedImage.resID} - ${checkedImage.alias ?? ''}`
    : checkedSnap?.resID
      ? `${checkedSnap.resID} - ${checkedSnap.name ?? ''}`
      : '-'

  const machineCategory = useMemo(
    () => (type === MachineType.CPU ? MachineType.CPU : MachineType.GPU),
    [type],
  )

  useEffect(() => {
    if (selectedMachine) {
      const key = (selectedMachine as any).rowKey || selectedMachine.key
      setExpandedRowKeys([key])
    }
  }, [selectedMachine])

  useEffect(() => {
    if (selectedGhostType) setGhostType(selectedGhostType)
  }, [selectedGhostType])

  const handleBootSourceSearchChange = (searchTerm: string) => {
    setBootSourceSearch(searchTerm)
    setCheckedImage(undefined)
    setCheckedSnap(undefined)
  }

  const isMachineDisabled = (machine: Machine) =>
    machine.maxAvailUnits === 0 ||
    Boolean(
      imageResId &&
        checkedImage &&
        enforceBootSourceResourceLimit &&
        getMinimumInstanceCountForBootSource({
          machine,
          count,
          source: checkedImage,
          supportsExtraResources: supportsExtraResourcesForBootSource,
        }) === undefined,
    )

  const handleRowClick = (machine: Machine & { rowKey?: string }) => {
    if (isMachineDisabled(machine)) return
    const key = getMachineRowKey(machine)
    const isExpanded = expandedRowKeys.includes(key)
    setExpandedRowKeys(isExpanded ? [] : [key])
    onChange(isExpanded ? undefined : machine)
    if (!imageResId) setCheckedImage(undefined)
    setCheckedSnap(undefined)
    setBootSourceSearch('')
  }

  const getDomains = useCallback(async () => {
    setLoading(true)
    try {
      const res = await API.getDomainList()
      if (res.code === 0) {
        setDomainInfos(
          (res.domainInfos || []).filter((info) => !HIDE_DOMAINS.includes(info.domain)),
        )
        setRegions((res.domainInfos || []).map((info) => info.domain))
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const getMachineList = useCallback(
    async (p: number = page, force: boolean) => {
      const requestId = ++requestIdRef.current
      if (imageResId) {
        onChange(undefined)
        setExpandedRowKeys([])
      }
      if (force) {
        setLoading(true)
      }
      try {
        let apiRegionFilterCriteria = 0
        if (regionFilterCriteria === REGION_GLOBAL) {
          apiRegionFilterCriteria = 0
        } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
          apiRegionFilterCriteria = 1
        } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
          apiRegionFilterCriteria = 2
        }

        const machineCategories =
          machineCategory === MachineType.CPU
            ? [MachineType.CPU]
            : [MachineType.GPU, MachineType.VGPU]
        const promises = machineCategories.map((category) =>
          API.getMachineList({
            page: p,
            per_page: pageSize,
            machine_category: category,
            payment_types: period != null ? [period] : undefined,
            class_merging: true,
            image_res_id: imageResId,
            domains: regions.filter((region) => !HIDE_DOMAINS.includes(region)),
            regions: regions.filter((region) => !HIDE_DOMAINS.includes(region)),
            region_filter_criteria: apiRegionFilterCriteria,
          }),
        )

        const results = await Promise.all(promises)
        if (requestId !== requestIdRef.current) return
        const newRawMachineClasses: { category: MachineType; classes: any[] }[] = []
        results.forEach((res, index) => {
          if (res.code === 0) {
            newRawMachineClasses.push({
              category: machineCategories[index],
              classes: res.machineClasses || [],
            })
          }
        })
        setRawMachineClasses(newRawMachineClasses)
      } catch (error) {
        if (requestId !== requestIdRef.current) return
        console.error(error)
        setRawMachineClasses([])
      } finally {
        if (requestId === requestIdRef.current) setLoading(false)
      }
    },
    [
      page,
      period,
      domainInfos,
      regions,
      machineCategory,
      pageSize,
      regionFilterCriteria,
      imageResId,
      onChange,
    ],
  )

  useEffect(() => {
    const newMachines: Machine[] = []
    rawMachineClasses.forEach((raw) => {
      raw.classes.forEach((m) => {
        newMachines.push(new Machine(raw.category, m))
      })
    })
    setAllMachines(newMachines)
  }, [rawMachineClasses, type])

  useEffect(() => {
    let filtered = allMachines

    // vGPU filter: when checked, show only VGPU machines
    if (machineCategory !== MachineType.CPU && isVgpuChecked) {
      filtered = filtered.filter((m) => m.machineCategory === MachineType.VGPU)
    }

    if (billingMethod === BILLING_METHODS.SUBSCRIPTION) {
      const requiredPaymentType =
        duration === DURATION.MONTHLY
          ? ItemPaymentType.Available30Days
          : ItemPaymentType.Available1Days

      filtered = filtered.filter((m) => {
        const items = m.items ?? []
        if (!items.length) return false
        return items.some((it) => it.paymentType === requiredPaymentType)
      })
    }

    if (machineCategory === MachineType.CPU) {
      const special = SPECIAL_CLIENT_MACHINE_SPECS.find((s) => s.userId === userId)
      if (special && special.cpu.length > 0) {
        filtered = filtered.filter((m) =>
          special.cpu.some((spec) => m.unitSpec.classTag.includes(spec)),
        )
      }
    } else if (machineCategory === MachineType.GPU) {
      const special = SPECIAL_CLIENT_MACHINE_SPECS.find((s) => s.userId === userId)
      const allowedSpecs: string[] = []

      const sourceSpecs = special || GPU_MACHINE_SPECS

      if (workloadTypes.includes('all') || workloadTypes.length === 0) {
        allowedSpecs.push(...sourceSpecs.inference, ...sourceSpecs.training, ...sourceSpecs.render)
      } else {
        if (workloadTypes.includes(MachineType.INFERENCE_GPU)) {
          allowedSpecs.push(...sourceSpecs.inference)
        }
        if (workloadTypes.includes(MachineType.TRAINING_GPU)) {
          allowedSpecs.push(...sourceSpecs.training)
        }
        if (workloadTypes.includes(MachineType.RENDER_GPU)) {
          allowedSpecs.push(...sourceSpecs.render)
        }
      }

      const uniqueAllowedSpecs = [...new Set(allowedSpecs)]

      // Collect all known specs across every workload category
      const allKnownSpecs = [
        ...GPU_MACHINE_SPECS.inference,
        ...GPU_MACHINE_SPECS.training,
        ...GPU_MACHINE_SPECS.render,
      ]
      const isUncategorized = (m: Machine) =>
        !allKnownSpecs.some((spec) => m.unitSpec.classTag.includes(spec))

      if (uniqueAllowedSpecs.length > 0) {
        filtered = filtered.filter(
          (m) =>
            uniqueAllowedSpecs.some((spec) => m.unitSpec.classTag.includes(spec)) ||
            isUncategorized(m),
        )
      } else if (!workloadTypes.includes('all') && workloadTypes.length > 0) {
        // No matching known specs for selected workloads, still show uncategorized machines
        filtered = filtered.filter(isUncategorized)
      }
    }

    const expanded: Machine[] = []
    filtered.forEach((machine) => {
      if (Array.isArray(machine.domains) && machine.domains.length > 1) {
        machine.domains.forEach((domain) => {
          const newMachine = Object.assign(Object.create(Object.getPrototypeOf(machine)), machine)
          newMachine.domains = [domain]
          ;(newMachine as any).rowKey = `${machine.key}-${domain}`
          expanded.push(newMachine)
        })
      } else if (Array.isArray(machine.domains) && machine.domains.length === 1) {
        ;(machine as any).rowKey = `${machine.key}-${machine.domains[0]}`
        expanded.push(machine)
      }
    })
    setFilteredMachines(expanded)
    onMachinesChange?.(expanded)
  }, [
    allMachines,
    billingMethod,
    duration,
    isVgpuChecked,
    machineCategory,
    onMachinesChange,
    userId,
    workloadTypes,
  ])

  useEffect(() => {
    if (!selectedMachine || loading || (!imageResId && allMachines.length === 0)) return

    const selectedKey = getRowKey(selectedMachine)
    const stillExists = filteredMachines.some((m) => getRowKey(m) === selectedKey)

    if (!stillExists) {
      setExpandedRowKeys([])
      onChange(undefined)
      if (!imageResId) setCheckedImage(undefined)
      setCheckedSnap(undefined)
    }
  }, [
    imageResId,
    allMachines.length,
    filteredMachines,
    loading,
    onChange,
    selectedMachine,
    setCheckedImage,
    setCheckedSnap,
  ])

  useEffect(() => {
    getDomains()
  }, [])

  useEffect(() => {
    if (domainInfos.length > 0) {
      getMachineList(1, true)
    }
    return () => {
      requestIdRef.current += 1
    }
  }, [scope, domainInfos, machineCategory, period, regions, regionFilterCriteria, getMachineList])

  return (
    <section
      data-ai-component="filter-panel"
      data-ai-state={loading ? 'loading' : filteredMachines.length > 0 ? 'ready' : 'empty'}
    >
      <div className="mb-4">
        <div className="flex flex-wrap items-end gap-6">
          {toolbarStart}
          <AcceleratorTypeSelector
            value={type}
            showVgpuToggle={!imageFiltersInToolbar}
            onChange={(value) => {
              onChange(undefined)
              setExpandedRowKeys([])
              onTypeChange(value)
            }}
            isVgpuChecked={isVgpuChecked}
            onVgpuCheckedChange={setIsVgpuChecked}
          />

          {machineCategory === MachineType.GPU && !imageFiltersInToolbar && (
            <WorkloadTypeSelector value={workloadTypes} onChange={setWorkloadTypes} />
          )}

          <div
            data-ai-component="region-filter"
            data-ai-action="select-region"
            data-ai-value={regionFilterCriteria}
          >
            <div className="text-secondary t-sb mb-2" id="region-filter-label">
              <Trans i18nKey="create.regionLabel">Region</Trans>
            </div>
            <div>
              <Dropdown
                rootClassName="t-sb w-60"
                className="h-11"
                options={[
                  {
                    label: t('common.region.groupGlobal'),
                    value: REGION_GLOBAL,
                    className: 't-sm',
                  },
                  {
                    label: t('common.region.groupDataDrive'),
                    value: REGION_DATA_DRIVE,
                    className: 't-sm',
                  },
                  {
                    label: t('common.region.groupMeshClustering'),
                    value: REGION_MESH_CLUSTERING,
                    className: 't-sm',
                  },
                  ...domainInfos.map((info, index) => ({
                    label: info.displayName || formatDomain(info.domain),
                    value: info.domain,
                    className: 't-sm',
                    separatorBefore: index === 0,
                  })),
                ]}
                value={regionFilterCriteria}
                onChange={(value) => {
                  const numValue = Number(value)
                  setRegionFilterCriteria(numValue)

                  if (numValue === REGION_GLOBAL) {
                    setRegions(domainInfos.map((info) => info.domain))
                  } else if (
                    numValue === REGION_DATA_DRIVE ||
                    numValue === REGION_MESH_CLUSTERING
                  ) {
                    setRegions([])
                  } else {
                    setRegions([numValue])
                  }
                }}
              />
            </div>
          </div>
          {imageFiltersInToolbar && (
            <>
              <div className={showImageFilters ? 'contents' : 'hidden'}>
                <ImageAttributeFilters
                  value={imageFilters}
                  onChange={(next) => {
                    setImageFilters(next)
                    setCheckedImage(undefined)
                  }}
                />
              </div>
              <button
                type="button"
                className="ml-auto flex h-11 items-center rounded-lg border border-neutral-200 p-3 text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                aria-label={t('create.imageView.toggleFilters')}
                aria-expanded={showImageFilters}
                onClick={() => setShowImageFilters(!showImageFilters)}
              >
                <span
                  aria-hidden="true"
                  className="block size-5 shrink-0 bg-current"
                  style={{
                    mask: `url(${ConfigureIcon}) center / contain no-repeat`,
                    WebkitMask: `url(${ConfigureIcon}) center / contain no-repeat`,
                  }}
                />
              </button>
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}

      {!loading && filteredMachines.length === 0 && (
        <EmptyView
          content={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg">No Data</Trans>
            </span>
          }
        />
      )}

      {!loading && filteredMachines.length > 0 && (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                rowExpandedBg: 'rgb(var(--color-table-row-selected))',
              },
            },
          }}
        >
          <Table
            rowKey="rowKey"
            columns={createColumns(
              Machine.getMachineType(type) === MachineType.GPU,
              domainInfos,
              billingMethod,
              duration,
            )}
            dataSource={filteredMachines}
            pagination={false}
            loading={loading}
            scroll={{ x: 'max-content' }}
            onRow={(machine) => ({
              'data-ai-component': 'machine-option',
              'data-ai-action': 'select-machine',
              'data-ai-state': getMachineAiState(
                machine,
                expandedRowKeys.includes(getMachineRowKey(machine)),
              ),
              'data-ai-value': getMachineRowKey(machine),
              'aria-disabled': isMachineDisabled(machine),
              title:
                imageResId && isMachineDisabled(machine)
                  ? t('create.imageView.insufficientResources')
                  : undefined,
              'aria-label': `${machine.displayTitle}, available units ${machine.maxAvailUnits}`,
              onClick: () => {
                if (machine.maxAvailUnits === 0) return
                handleRowClick(machine)
              },
            })}
            rowClassName={(record) => {
              const key = getMachineRowKey(record)
              const isSelected = expandedRowKeys.includes(key)
              return cn('cursor-pointer', {
                selected: isSelected,
                'ant-table-row-fixed': isSelected,
              })
            }}
            rowSelection={
              imageResId
                ? {
                    type: 'radio',
                    selectedRowKeys: selectedMachine ? [getMachineRowKey(selectedMachine)] : [],
                    onSelect: (record) => handleRowClick(record),
                    getCheckboxProps: (record) => ({ disabled: isMachineDisabled(record) }),
                  }
                : undefined
            }
            expandable={{
              showExpandColumn: !imageResId,
              expandedRowKeys: imageResId ? [] : expandedRowKeys,
              onExpand: (expanded, record) => {
                const key = getMachineRowKey(record)
                if (record.maxAvailUnits === 0) return
                setExpandedRowKeys(expanded ? [key] : [])
                onChange(expanded ? record : undefined)
                setCheckedImage(undefined)
                setCheckedSnap(undefined)
                setBootSourceSearch('')
              },
              expandIcon: ({ expanded, record }) => (
                <Radio checked={expanded} disabled={record.maxAvailUnits === 0} />
              ),
              expandedRowRender: (machine) => (
                <div
                  className="bg-panel-white rounded-2xl p-6"
                  data-ai-component="machine-configuration-panel"
                  data-ai-state={
                    checkedImage || checkedSnap ? 'boot-source-selected' : 'needs-boot-source'
                  }
                  data-ai-value={getMachineRowKey(machine)}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                    <div className="flex min-w-0 items-center gap-4">
                      <ButtonRadio
                        value={ghostType}
                        size="small"
                        className="shrink-0 [&>button]:px-4 [&>button]:py-2"
                        items={[
                          {
                            label: t(
                              paginateImagesOnServer
                                ? 'create.typeLabelAllImages'
                                : 'create.typeLabelOfficialImage',
                            ),
                            value: GhostType.Image,
                            buttonProps: {
                              role: 'radio',
                              'aria-checked': ghostType === GhostType.Image,
                              'data-ai-action': 'select-boot-source-type',
                              'data-ai-state':
                                ghostType === GhostType.Image ? 'selected' : 'unselected',
                              'data-ai-value': paginateImagesOnServer
                                ? 'all-images'
                                : 'official-image',
                            },
                          },
                          {
                            label: t('create.typeLabelCustomImage'),
                            value: GhostType.CustomImage,
                            buttonProps: {
                              role: 'radio',
                              'aria-checked': ghostType === GhostType.CustomImage,
                              'data-ai-action': 'select-boot-source-type',
                              'data-ai-state':
                                ghostType === GhostType.CustomImage ? 'selected' : 'unselected',
                              'data-ai-value': 'custom-image',
                            },
                          },
                          {
                            label: t('create.typeLabelSnapshot'),
                            value: GhostType.Snapshot,
                            buttonProps: {
                              role: 'radio',
                              'aria-checked': ghostType === GhostType.Snapshot,
                              'data-ai-action': 'select-boot-source-type',
                              'data-ai-state':
                                ghostType === GhostType.Snapshot ? 'selected' : 'unselected',
                              'data-ai-value': 'snapshot',
                            },
                          },
                        ]}
                        rootProps={{
                          role: 'radiogroup',
                          'aria-label': 'Boot source type',
                          'data-ai-component': 'boot-source-type-options',
                        }}
                        onChange={(value) => {
                          setGhostType(value)
                          setCheckedImage(undefined)
                          setCheckedSnap(undefined)
                        }}
                      />
                      {paginateImagesOnServer && ghostType !== GhostType.Snapshot ? (
                        <ImageKeywordSearch
                          value={imageKeywords}
                          onChange={(keywords) => {
                            setImageKeywords(keywords)
                            setCheckedImage(undefined)
                          }}
                        />
                      ) : (
                        <div
                          role="search"
                          className="flex h-8 w-[174px] shrink-0 items-center gap-2 rounded-lg border border-neutral-200 bg-panel-white px-2 focus-within:border-secondary"
                          data-ai-component="boot-source-search"
                        >
                          <SearchIcon className="size-5 shrink-0" aria-hidden="true" />
                          <input
                            type="search"
                            value={bootSourceSearch}
                            placeholder={t('create.bootSourceSearchPlaceholder', 'Search')}
                            aria-label={t('create.bootSourceSearchLabel', 'Search boot sources')}
                            className="min-w-0 flex-1 bg-transparent t-xs text-primary outline-none placeholder:text-tertiary [&::-webkit-search-cancel-button]:appearance-none"
                            data-ai-action="search-boot-sources"
                            onChange={(event) => handleBootSourceSearchChange(event.target.value)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex min-w-0 max-w-xs items-center justify-center gap-x-2 rounded-[48px] bg-neutral-50 px-4 py-2 t-x-sb">
                      <span className="shrink-0 text-tertiary">
                        <Trans i18nKey="create.selectedLabel">Selected</Trans>
                      </span>
                      <Tooltip title={selectedBootSourceLabel} trigger={['hover', 'focus']}>
                        <span className="min-w-0 truncate text-primary" tabIndex={0}>
                          {selectedBootSourceLabel}
                        </span>
                      </Tooltip>
                    </div>
                  </div>

                  {(ghostType === GhostType.Image || ghostType === GhostType.CustomImage) && (
                    <ImageSelector
                      value={checkedImage}
                      machine={machine}
                      type={Machine.getMachineType(type)}
                      count={count}
                      onChange={setCheckedImage}
                      onImagesChange={onImagesChange}
                      ghostType={ghostType}
                      enforceResourceLimit={enforceBootSourceResourceLimit}
                      supportsExtraResources={supportsExtraResourcesForBootSource}
                      emptyMessageVariant={imageEmptyMessageVariant}
                      searchTerm={bootSourceSearch}
                      paginateOnServer={paginateImagesOnServer}
                      keywords={imageKeywords}
                      filters={imageFiltersInToolbar ? imageFilters : undefined}
                    />
                  )}

                  {ghostType === GhostType.Snapshot && (
                    <SnapshotSelector
                      value={checkedSnap}
                      machine={machine}
                      type={Machine.getMachineType(type)}
                      onChange={setCheckedSnap}
                      onSnapshotsChange={onSnapshotsChange}
                      additionalSnapshots={teamSnapshots}
                      count={count}
                      enforceResourceLimit={enforceBootSourceResourceLimit}
                      supportsExtraResources={supportsExtraResourcesForBootSource}
                      emptyMessageVariant={imageEmptyMessageVariant}
                      searchTerm={bootSourceSearch}
                    />
                  )}
                </div>
              ),
            }}
          />
        </ConfigProvider>
      )}

      {!loading && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-secondary t-sm flex items-center gap-1"></div>
          {filteredMachines.length > 0 && (
            <Pagination
              size={pageSize}
              total={filteredMachines.length}
              current={page}
              onChange={(page) => {
                getMachineList(page, false)
              }}
            />
          )}
        </div>
      )}
    </section>
  )
}
